// Disciplinary actions (Phase 5) — role-adaptive. HR issues + manages; employees
// view their own record.
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { Plus, Gavel } from 'lucide-react';
import { Card, SectionTitle, CenterSpinner, PrimaryBtn, OutlineBtn, Pill } from './ui';
import { EmptyState } from './components';
import { AG } from './theme/tokens';
import { api } from './api';
import { useAuth } from './auth';

const TYPES = ['Verbal', 'Written', 'FinalWarning', 'Suspension', 'Termination'];
const tone = (t) => (t === 'Termination' || t === 'Suspension' ? 'error' : t === 'FinalWarning' ? 'warning' : 'info');

export default function Disciplinary({ showToast }) {
  const { isHR } = useAuth();
  return isHR ? <DisciplinaryHR showToast={showToast} /> : <DisciplinaryEmployee />;
}

function Item({ a, onRevoke }) {
  return (
    <Card style={{ padding: 14 }}>
      <div className="flex items-center justify-between">
        <div>
          <div style={{ fontWeight: 600, color: AG.text }}>
            {a.employeeName ? `${a.employeeName} · ` : ''}<Pill tone={tone(a.type)}>{a.type}</Pill>{a.status === 'Revoked' && <span style={{ marginLeft: 6 }}><Pill tone="neutral">Revoked</Pill></span>}
          </div>
          <div style={{ fontSize: 13, color: AG.n600, marginTop: 4 }}>{a.reason}</div>
          <div style={{ fontSize: 12, color: AG.n500, marginTop: 2 }}>Effective {a.effectiveDate}{a.expiresAt ? ` · expires ${a.expiresAt}` : ''}</div>
        </div>
        {onRevoke && a.status === 'Active' && <OutlineBtn onClick={() => onRevoke(a)}>Revoke</OutlineBtn>}
      </div>
    </Card>
  );
}

function DisciplinaryEmployee() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.myDisciplinary().then(setRows).catch(() => setRows([])); }, []);
  if (!rows) return <CenterSpinner />;
  return (
    <div className="space-y-4 fade-up">
      <SectionTitle title="My Record" sub="Disciplinary actions on your file" />
      {rows.length === 0 ? <Card><EmptyState icon={Gavel} title="Clean record" desc="You have no disciplinary actions on file." /></Card> : (
        <div style={{ display: 'grid', gap: 8 }}>{rows.map((a) => <Item key={a.id} a={a} />)}</div>
      )}
    </div>
  );
}

function DisciplinaryHR({ showToast }) {
  const [rows, setRows] = useState(null);
  const [people, setPeople] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.disciplinaryAll().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); api.employees().then((r) => setPeople(Array.isArray(r) ? r : r.rows || [])).catch(() => setPeople([])); }, []);
  const issue = async () => {
    if (!form.employeeId || !form.reason.trim() || busy) return; setBusy(true);
    try { await api.issueDisciplinary(form); showToast?.('Action recorded'); setForm(null); load(); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const revoke = async (a) => { try { await api.updateDisciplinary(a.id, { status: 'Revoked' }); load(); } catch (e) { showToast?.(e.message); } };
  if (!rows) return <CenterSpinner />;
  return (
    <div className="space-y-4 fade-up">
      <div className="flex items-center justify-between" style={{ gap: 12, flexWrap: 'wrap' }}>
        <SectionTitle title="Disciplinary" sub="Issue and track disciplinary actions" />
        <PrimaryBtn onClick={() => setForm({ employeeId: '', type: 'Verbal', reason: '', notes: '', effectiveDate: '', expiresAt: '' })}><Plus size={14} style={{ marginRight: 6 }} /> New action</PrimaryBtn>
      </div>
      {rows.length === 0 ? <Card><EmptyState icon={Gavel} title="No actions" desc="Disciplinary actions you issue appear here." /></Card> : (
        <div style={{ display: 'grid', gap: 8 }}>{rows.map((a) => <Item key={a.id} a={a} onRevoke={revoke} />)}</div>
      )}
      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>New disciplinary action</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Employee</div>
              <select value={form.employeeId} onChange={(e) => setForm({ ...form, employeeId: e.target.value })} className="ag-input" style={{ marginTop: 4, marginBottom: 12 }}>
                <option value="">Select employee…</option>
                {people.map((p) => <option key={p.id} value={p.id}>{p.name}{p.dept ? ` · ${p.dept}` : ''}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-2" style={{ marginBottom: 12 }}>
                <div><div style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Type</div><select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="ag-input" style={{ marginTop: 4 }}>{TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
                <TextField size="small" type="date" label="Effective" InputLabelProps={{ shrink: true }} value={form.effectiveDate} onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })} />
              </div>
              <TextField fullWidth size="small" label="Reason" multiline minRows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} sx={{ mb: 1.5 }} />
              <TextField fullWidth size="small" type="date" label="Expires (optional)" InputLabelProps={{ shrink: true }} value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} />
            </div>
          )}
        </DialogContent>
        <DialogActions><OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={issue} disabled={busy}>Record</PrimaryBtn></DialogActions>
      </Dialog>
    </div>
  );
}
